import { useEffect, useState } from "react";
import { DataTable } from "@/components/tables";
import { UploadUserGroupResponse, UploadUserResponse } from "./types";
import { SuccessCircleCheck, InfoIcon } from "@/assets/icons";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Alert,
  AlertDescription,
} from "@altimateai/lego";

interface Props {
  failures?: UploadUserResponse["failures"] | UploadUserGroupResponse["failures"];
  success?: UploadUserResponse["success"] | UploadUserGroupResponse["success"];
  uploadType: "user" | "userGroup";
}

const UploadStatusModal = ({ failures, success, uploadType }: Props) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(Boolean(failures?.length));
  }, [failures]);

  const failureFields =
    uploadType === "user"
      ? [
          { field: "email", label: "Email" },
          { field: "group_name", label: "Group Name" },
          { field: "reason", label: "Reason" },
        ]
      : [
          { field: "entity_type", label: "Entity Type" },
          { field: "group_name", label: "Group Name" },
          { field: "match_type", label: "Rule Type" },
          { field: "reason", label: "Reason" },
        ];
  const successFields =
    uploadType === "user"
      ? [
          { field: "email", label: "Email" },
          { field: "group_name", label: "Group Name" },
        ]
      : [
          { field: "entity_type", label: "Entity Type" },
          { field: "group_name", label: "Group Name" },
          { field: "match_type", label: "Rule Type" },
          { field: "entity", label: "Entity" },
          { field: "description", label: "Description" },
        ];

  const gridTemplateColumnsForFailureTable = failureFields
    .map(f => (f.field === "reason" ? "minmax(0, 3fr)" : "minmax(0, 1fr)"))
    .join(" ");
  const gridTemplateColumnsForSuccessTable = successFields.map(() => "1fr").join(" ");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="!al-max-w-[90vw]">
        <DialogHeader>
          <DialogTitle>Upload Status</DialogTitle>
        </DialogHeader>
        <div className="al-space-y-6">
          <Alert variant="destructive">
            <InfoIcon className="al-h-4 al-w-4" />
            <AlertDescription>
              Following issues are found in the uploaded CSV. Please rectify the issues and upload
              again.
            </AlertDescription>
          </Alert>
          <DataTable
            onRowClick={() => {}}
            resetPage={() => {}}
            gridTemplateColumns={gridTemplateColumnsForFailureTable}
            data={{ items: failures || [] }}
            header={failureFields.map((field, i) => ({
              id: i,
              uid: field.field,
              label: field.label,
              render: (item: NonNullable<typeof failures>["0"]) => (
                <div className={field.field === "reason" ? "al-text-destructive" : ""}>
                  {item[field.field as keyof NonNullable<typeof failures>["0"]]}
                </div>
              ),
            }))}
          />

          {success?.length ? (
            <div className="al-space-y-4">
              <Alert>
                <SuccessCircleCheck className="al-h-4 al-w-4" />
                <AlertDescription>
                  {uploadType === "user"
                    ? "Following users are successfully added"
                    : "Following teams are successfully added"}
                </AlertDescription>
              </Alert>
              <DataTable
                onRowClick={() => {}}
                resetPage={() => {}}
                data={{ items: success || [] }}
                gridTemplateColumns={gridTemplateColumnsForSuccessTable}
                header={successFields.map((field, i) => ({
                  id: i,
                  uid: field.field,
                  label: field.label,
                  render: (item: NonNullable<typeof success>[0]) => (
                    <div>{item[field.field as keyof NonNullable<typeof success>[0]]}</div>
                  ),
                }))}
              />
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Ok
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UploadStatusModal;
